import { useState } from "react";
import { APIProvider, InfoWindow, Map, Marker } from "@vis.gl/react-google-maps";
import { Loader } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GOOGLE_MAPS_API_KEY } from "shared/constants";
import { CarriersFilter } from "hooks/useCarriersFilter";
import useGetCarriersMap from "hooks/useGetCarriersMap";
import { CarrierMapPointDto } from "models/dto/CarrierMapPointDto";

import styles from "./CarrierMap.module.scss";

type CarrierMapProps = {
  carriersFilter: CarriersFilter;
};

const europeCenter = { lat: 50.5, lng: 10.5 };

const CarrierMap = ({ carriersFilter }: CarrierMapProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { carrierPoints, isLoading } = useGetCarriersMap(carriersFilter);
  const [activePoint, setActivePoint] = useState<CarrierMapPointDto | null>(
    null
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.overlay}>
        {isLoading
          ? t("carrierMap.loading")
          : t("carrierMap.carriersOnMap", { count: carrierPoints.length })}
      </div>

      {isLoading ? <Loader active inline="centered" size="small" /> : null}

      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          className={styles.map}
          defaultZoom={5}
          defaultCenter={europeCenter}
          gestureHandling="greedy"
          disableDefaultUI={false}
        >
          {carrierPoints.map((point) => (
            <Marker
              key={point.id}
              position={{ lat: point.latitude, lng: point.longitude }}
              title={point.name}
              onClick={() => setActivePoint(point)}
            />
          ))}

          {activePoint ? (
            <InfoWindow
              position={{
                lat: activePoint.latitude,
                lng: activePoint.longitude,
              }}
              onCloseClick={() => setActivePoint(null)}
            >
              <div className={styles.infoWindow}>
                <div className={styles.infoName}>{activePoint.name}</div>
                <div className={styles.infoLocation}>
                  {[activePoint.city, activePoint.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/carriers/${activePoint.id}`)}
                >
                  {t("carrierMap.openCarrier")}
                </button>
              </div>
            </InfoWindow>
          ) : null}
        </Map>
      </APIProvider>
    </div>
  );
};

export default CarrierMap;
